import { MapToolsStore } from '@openlayers/MapToolsStore';
import { Events } from '@shared/Events';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import './map.scss';
import { makeLayers } from './map/layers';
import { Map2dUI } from './ui/map-ui';
import { MassMap } from './ui/mass-grid/MassMap';

import { MapStationLayer } from '@openlayers/MapStationLayer';
import { MapWrapper } from '@openlayers/MapWrapper';
import { IMapTilelayer, TileLayerService } from '@openlayers/tile-layers/TileLayerService';
import { MapSearch } from '@pages/map/map-search';
import { Map2dPageStateHandler } from '@pages/map/state/Map2dPageStateHandler';
import { Map2dRootStore } from '@pages/map/state/RootStore';
import { DefaultCursorTool } from '@pages/map/tools/tool-default-cursor';
import { DebugFlag } from '@shared/DebugFlag';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { CanvasDrawing } from '@shared/user-state/comments/CanvasDrawing';
import { PageStateFacade } from '@shared/user-state/PageStateFacade';
import { PageStateService } from '@shared/user-state/PageStateService';
import * as React from 'react';
import * as ol from 'ol';
import { lumiDbStore } from '@shared/reality-capture/lumidb-store';
import { demStore } from '@shared/reality-capture/dem-store';

let mapWrapper: MapWrapper = null;
let mapUI: Map2dUI = null;
let mapStation: MapStationLayer = null;
let mapTools: MapToolsStore = null;
let mapRootStore: Map2dRootStore = null;
let massMap: MassMap = null;
let mapStateFacade: PageStateFacade<IMapPageState> = null;
let extrasLayer: VectorLayer<any> = null;
let mapDrawing: CanvasDrawing = null;

export { extrasLayer, mapDrawing, mapRootStore, mapStateFacade, mapStation, mapTools, mapUI, mapWrapper, massMap };

declare global {
    // tslint:disable-next-line
    interface Window {
        mapWrapper: MapWrapper;
        mapUI: Map2dUI;
        mapTools: MapToolsStore;
        mapStateFacade: PageStateFacade<IMapPageState>;
        extrasLayer: VectorLayer<any>;
        olVersion: string;
    }
}

async function initMap(state: IMapPageState, tileLayers: IMapTilelayer[]) {
    window.olVersion = ol.VERSION;

    mapWrapper = new MapWrapper();
    mapStateFacade = new PageStateFacade('MAP', new Map2dPageStateHandler());
    mapRootStore = new Map2dRootStore();
    mapDrawing = new CanvasDrawing(document.querySelector('#map-canvas').parentElement);

    // restore view state before init so map wouldn't jump around
    let viewState = null;
    if (state && state.data && state.data.map && state.data.map.view) {
        viewState = state.data.map.view;
        mapWrapper.init('map-canvas', [viewState.lat, viewState.lon], viewState.zoom, tileLayers);
    } else {
        mapWrapper.init('map-canvas', [null, null], null, tileLayers);
        await mapWrapper.fitProjectBounds();
    }

    // init layers
    extrasLayer = new VectorLayer({
        source: new VectorSource(),
    });

    mapWrapper.setFeatureLayers(makeLayers(mapWrapper.getInstance()));
    mapWrapper.addLayer(extrasLayer, 90, 'extras');

    // map station
    mapStation = new MapStationLayer();
    mapWrapper.addLayer(mapStation.layer, 91, 'map-station');
    Events.on('map-click-marker-position', drawStationLine);
    Events.on('activeAlignmentChange.station', drawStationLine);

    function drawStationLine() {
        const alignment = mapWrapper.featureLayers.alignments.getActiveAlignment();

        if (alignment && alignment.modelId > 0) {
            const clickMarkerCoords = mapWrapper.clickMarkerLayer.clickMarkerPosition;
            if (clickMarkerCoords) {
                mapStation.fetchAndDrawStationLine(clickMarkerCoords[0], clickMarkerCoords[1], alignment.modelId, alignment.index);
            }
        }
    }

    // mass
    massMap = new MassMap(mapWrapper.featureLayers.models);

    // UI
    mapTools = new MapToolsStore();

    const getSelectedPointClouds = (): string[] => {
        return mapWrapper.tileLayers.activeUuids
            .map((uuid: string) => mapWrapper.tileLayers.layersDefinitions.find(l => l.uuid === uuid)?.fileUuid)
            .filter((uuid): uuid is string => Boolean(uuid));
    };

    const getSelectedDemUuids = (): string[] => {
        return mapWrapper.tileLayers.activeUuids
            .map((uuid: string) => {
                const layer = mapWrapper.tileLayers.layersDefinitions.find(l => l.uuid === uuid);
                return layer?.demUrl ? layer.fileUuid : null;
            })
            .filter((uuid): uuid is string => Boolean(uuid));
    };

    mapRootStore.getTreeData();

    // Set tile layers in dem store
    demStore.setTileLayers(mapWrapper.tileLayers);

    // init lumiDB
    mapTools.init(mapWrapper, () => mapUI?.mapPanels?.panels?.models?.getCheckedFileIds() ?? [], () => mapUI?.mapPanels?.panels?.models?.getChecked() ?? [], getSelectedPointClouds, getSelectedDemUuids);
    if (lumiDbStore.available) {
        mapRootStore.getTreeData().then((treeData: ITreeData) => lumiDbStore.initLumiDb(treeData));
    }

    // bind mouse events
    const defaultCursorTool = new DefaultCursorTool();
    mapWrapper.events.onClick(defaultCursorTool.handleMapClick);
    mapWrapper.events.onMouseMove(defaultCursorTool.handleMapMouseMove);
    mapWrapper.events.onRightClick(defaultCursorTool.handleMapRightClick);
    mapWrapper.events.onZoom(defaultCursorTool.handleMapZoom);
    mapWrapper.events.onRotation(
        _.throttle(e => {
            // force redrawing of image icons so that direction arrow will point in right direction
            // rotating map without forced change trigger will only redraw sometimes
            mapWrapper.featureLayers.images.layerSource.dispatchEvent('change');
        }, 300)
    );
    mapWrapper.events.triggerOnZoom();
    mapWrapper.events.onMove(
        _.throttle(_e => {
            Events.trigger('map-move', { center: mapWrapper.getCenter(), zoom: mapWrapper.getZoom() });
        }, 500)
    );

    // bind keyboard events
    $(document).on('keydown.map', e => {
        if ((e.ctrlKey || e.metaKey) && e.keyCode == 65) {
            // ctrl + a
            if (window.document.activeElement.tagName === 'INPUT' || window.document.activeElement.tagName === 'TEXTAREA') {
                return;
            }
            e.preventDefault();
            mapWrapper.fitVisible(mapTools.getDrawingsExtent());
        }

        if (e.ctrlKey && e.keyCode === 32) {
            MapSearch.open();
        }
        if (e.keyCode === 27) {
            ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
        }
    });

    mapRootStore.init().then(() => {
        const uiHolder = document.createElement('div');
        uiHolder.style.position = 'absolute';
        uiHolder.style.overflow = 'visible';
        uiHolder.style.height = '0px';
        uiHolder.style.width = '0px';
        document.querySelector('#map-canvas-wrapper').appendChild(uiHolder);
        ReactUtils.safeRender(
            React.createElement(Map2dUI, {
                ref: el => {
                    mapUI = el;
                    window.mapUI = mapUI;
                },
            }),
            uiHolder
        );

        mapStateFacade.store.fetchItemsSimple();
        mapWrapper.featureLayers.vehicles.fetchAndDrawFolder();

        // expose globals
        window.mapWrapper = mapWrapper;
        window.mapStateFacade = mapStateFacade;
        window.mapTools = mapTools;

        setTimeout(() => {
            mapStateFacade.handler.restoreState(state); // panels might not be ready for restore, but each should handle it itself (use queuedRestoreState)
        }, 50);

        setTimeout(() => {
            // trigger fetch just in case here, it will be called by other component before this executes
            ProjectCodesService.fetchCodes();
        }, 2000);

        mapStateFacade.liveUserState.startPolling(3000);
    });
}

async function loadInitialPageState() {
    const vars: any = utils.getUrlVars();
    const confirmLoad = vars.hasOwnProperty('confirmStateLoad') ? vars.confirmStateLoad === 'true' : true;
    const fallbackToLiveState = !DebugFlag.mapNoState;
    return await PageStateService.fetchStateOrLiveState(vars.state, 'MAP', confirmLoad, fallbackToLiveState);
}

async function onLoad() {
    const vars: any = utils.getUrlVars();
    // if loading state on mobile, switch to tablet version
    if (vars?.state && window.innerWidth < 600) {
        window.location.href = window.location.href.replace('main.htm', 'mobile2.htm');
        return;
    }

    const tileLayers = await TileLayerService.fetchLayers();

    try {
        const initialState = await loadInitialPageState();
        initMap(initialState, tileLayers);
    } catch (err) {
        console.warn('[ map ] Failed to load initial state', err);
        initMap(null, tileLayers);
    }
}

onLoad();
